import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Phone, ShieldCheck, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import { translations } from '../../lib/i18n/translations';
import { useVoiceTemplates, type VoiceTemplate } from '../../lib/voice/hooks';
import { dialManual } from '../../lib/voice/api';

type VoiceT = typeof translations.en.settings.voice;

interface Props {
  t: VoiceT;
  lang: string;
  leadId: string;
  onClose: () => void;
  onDialed?: () => void;
}

/** Categorizza l'errore backend per mostrare il messaggio giusto (no crash). */
type DialErrorKind = 'consent' | 'tos' | 'generic';

function classifyError(message: string): DialErrorKind {
  const m = message.toLowerCase();
  if (/consent|consenso|opt.?out|do.?not.?call/.test(m)) return 'consent';
  if (/\bt(os|erms?)\b|term|acceptance|not_accepted|accett/.test(m)) return 'tos';
  return 'generic';
}

function trI18n(s: VoiceTemplate['label'] | null, lang: string): string {
  if (!s) return '';
  return s[lang] ?? s.it ?? s.en ?? '';
}

/**
 * Modale "Chiama con AI" sulla scheda lead. Mostra SOLO i template dialabili
 * (purpose commercial/transactional, mai inbound_reception) e lancia
 * dialManual({lead_id, template_id}). Gli errori del backend (consenso mancante,
 * T&C non accettati, nessun binding) vengono mappati a messaggi chiari — mai crash.
 */
export function DialWithAiModal({ t, lang, leadId, onClose, onDialed }: Props) {
  const navigate = useNavigate();
  const { templates, loading, error: loadError } = useVoiceTemplates();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialing, setDialing] = useState(false);
  const [dialError, setDialError] = useState<{ kind: DialErrorKind; raw: string } | null>(null);
  const [done, setDone] = useState(false);

  // Solo template dialabili: escludi la reception inbound (non ha senso comporre).
  const dialable = useMemo(
    () => templates.filter((tpl) => tpl.purpose !== 'inbound_reception'),
    [templates],
  );

  const handleDial = async () => {
    if (!selectedId) return;
    setDialing(true);
    setDialError(null);
    try {
      await dialManual({ lead_id: leadId, template_id: selectedId });
      setDone(true);
      onDialed?.();
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      setDialError({ kind: classifyError(raw), raw });
    } finally {
      setDialing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md max-w-lg w-full text-[#37352f] max-h-[85vh] flex flex-col">
        <div className="px-5 pt-5 pb-3 border-b border-[rgba(55,53,47,0.09)]">
          <h3 className="text-[15px] font-semibold flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#787774]" />
            {t.dial.button}
          </h3>
          <p className="text-[12px] text-[#787774] mt-1">{t.dial.pickTemplateHint}</p>
        </div>

        <div className="px-5 py-3 overflow-y-auto flex-1">
          {done ? (
            <div className="py-8 text-center">
              <CheckCircle2 className="w-6 h-6 text-[#0f7b6c] mx-auto mb-2" />
              <p className="text-[13px] text-[#37352f]">{t.dial.queued}</p>
            </div>
          ) : loading ? (
            <div className="py-8 text-center text-[13px] text-[#787774]">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            </div>
          ) : loadError ? (
            <div className="text-[13px] text-[#e03e3e] py-6 text-center">{loadError}</div>
          ) : dialable.length === 0 ? (
            <div className="text-[13px] text-[#787774] py-8 text-center">{t.dial.noTemplates}</div>
          ) : (
            <ul className="space-y-2">
              {dialable.map((tpl) => {
                const active = selectedId === tpl.id;
                return (
                  <li key={tpl.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(tpl.id)}
                      className={`w-full text-left border rounded-md px-3 py-2.5 flex items-start gap-3 transition-colors ${
                        active
                          ? 'border-[#37352f] bg-[rgba(55,53,47,0.04)]'
                          : 'border-[rgba(55,53,47,0.09)] hover:bg-[rgba(55,53,47,0.03)]'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-medium truncate">
                            {trI18n(tpl.label, lang)}
                          </span>
                          {tpl.is_featured && <Sparkles className="w-3 h-3 text-[#2383E2] shrink-0" />}
                          {tpl.disclaimer_verified && (
                            <ShieldCheck
                              className="w-3 h-3 text-[#0f7b6c] shrink-0"
                              aria-label={t.agent.disclaimerVerified}
                            />
                          )}
                        </div>
                        <p className="text-[12px] text-[#787774] mt-0.5 line-clamp-2">
                          {trI18n(tpl.one_liner ?? tpl.description, lang)}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {dialError && (
            <div className="text-[12px] text-[#e03e3e] bg-[rgba(224,62,62,0.06)] px-2.5 py-2 rounded mt-3">
              {dialError.kind === 'consent' ? (
                <span>{t.dial.consentMissing}</span>
              ) : dialError.kind === 'tos' ? (
                <div className="space-y-1.5">
                  <p>{t.dial.tosMissing}</p>
                  <button
                    type="button"
                    onClick={() => navigate('/settings/voice-setup')}
                    className="inline-flex items-center gap-1 text-[#37352f] font-medium hover:underline"
                  >
                    {t.dial.tosLink}
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                // Il core risponde già con messaggi chiari e localizzati (es. "Il lead
                // non ha un numero"): mostrali. Generico solo per errori di rete/http.
                <span>{/^http_\d+$/.test(dialError.raw) || !dialError.raw ? t.dial.error : dialError.raw}</span>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[rgba(55,53,47,0.09)] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={dialing}
            className="text-[13px] text-[#37352f] px-3 py-1.5 hover:bg-[rgba(55,53,47,0.06)] rounded disabled:opacity-50"
          >
            {done ? t.calls.close : t.hitl.cancel}
          </button>
          {!done && (
            <button
              type="button"
              onClick={handleDial}
              disabled={!selectedId || dialing}
              className="text-[13px] text-white bg-[#37352f] hover:bg-black px-3 py-1.5 rounded font-medium flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {dialing && <Loader2 className="w-3 h-3 animate-spin" />}
              {dialing ? t.dial.dialing : t.dial.confirm}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
